<template>
    <div class="view">
        <transition name="fade" mode="out-in">
            <LoadingSpinner
                v-if="loading"
                key="loading-spinner"
                color="#016ec1"
            />

            <div v-else-if="error" key="error-wrap" class="error-wrap">
                <h2 class="title">Oops!</h2>
                <p>There was a problem.</p>
            </div>

            <div
                v-else-if="success"
                key="form-success-wrap"
                class="form-success-wrap"
            >
                <template v-if="action === 'question'">
                    <h2 class="title">Success!</h2>
                    <p>
                        Thank you for your question. We'll get back to you as
                        soon as we can!
                    </p>
                </template>
                <template v-else>
                    <h2 class="title">Success!</h2>
                    <p>Your appointment is set for:</p>
                    <ul>
                        <li><span>Date:</span> {{ appointment.date }}</li>
                        <li><span>Time:</span> {{ appointment.time }}</li>
                    </ul>
                    <p>See you then!</p>
                </template>
            </div>

            <div v-else key="main-wrap" class="main-wrap">
                <img
                    :src="require(`@/assets/img/hero-schedule-today.jpg`)"
                    class="hero-image"
                    alt="Banner image which says 'Schedule Today'"
                />

                <ActionTypeButtons :selected="action" />

                <div class="gray-bg">
                    <div class="please-select">
                        {{ pleaseSelectHint }}
                    </div>

                    <div class="divot" />

                    <div class="appointment-and-form">
                        <transition name="fade">
                            <SelectAppointmentSlot
                                v-if="action !== 'question'"
                                key="select-appointment"
                                :datetime.sync="form.datetime"
                                :action="action"
                                :dealer="dealer"
                                :validation="$v.form.datetime"
                            />
                        </transition>

                        <ContactDetailsForm
                            :form.sync="form"
                            :action="action"
                            :validation="$v.form"
                        />
                    </div>

                    <transition name="fade">
                        <div
                            v-if="action !== 'question'"
                            key="send-text-reminder"
                            class="text-reminder-container"
                        >
                            <CheckboxWrap
                                id="send_text_reminder"
                                v-model="form.sendTextReminder"
                                label="Send Me a Reminder Text"
                                class="send-text-reminder"
                                :validation="$v.form.sendTextReminder"
                            />
                        </div>
                    </transition>

                    <SubmitButton
                        :action="action"
                        :submitting="submitting"
                        @submit="submit"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/plugins/axios';
import { validationMixin } from 'vuelidate';
import {
    required,
    requiredIf,
    requiredUnless,
    email,
    minLength,
    maxLength,
    helpers,
} from 'vuelidate/lib/validators';
import format from 'date-fns/format';
import { sleep } from '@/services/helpers';
import { Actions } from '@/services/enums';
import LoadingSpinner from '@/components/loading-spinner';
import ActionTypeButtons from '@/components/action-type-buttons';
import SelectAppointmentSlot from '@/components/select-appointment-slot';
import ContactDetailsForm from '@/components/contact-details-form';
import CheckboxWrap from '@/components/form/checkbox-wrap';
import SubmitButton from '@/components/submit-button';

const validPhoneNumber = helpers.regex('phone', /[0-9]{10}/);

export default {
    name: 'MainView',

    components: {
        LoadingSpinner,
        ActionTypeButtons,
        SelectAppointmentSlot,
        ContactDetailsForm,
        CheckboxWrap,
        SubmitButton,
    },

    mixins: [validationMixin],

    validations: {
        form: {
            datetime: {
                required: requiredUnless(function () {
                    return this.action === Actions.Question;
                }),
            },

            firstName: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(100),
            },
            lastName: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(100),
            },
            email: {
                required,
                email,
            },
            phone: { required, phone: validPhoneNumber },

            sendTextReminder: {},

            address: {
                required: requiredIf(function () {
                    return this.action === Actions.RemoteAppointment;
                }),
                minLength: minLength(2),
                maxLength: maxLength(100),
            },
            city: {
                required: requiredIf(function () {
                    return this.action === Actions.RemoteAppointment;
                }),
                minLength: minLength(2),
                maxLength: maxLength(100),
            },
            state: {
                required: requiredIf(function () {
                    return this.action === Actions.RemoteAppointment;
                }),
                state: minLength(2),
                state1: maxLength(2),
            },
            zip: {
                required: requiredIf(function () {
                    return this.action === Actions.RemoteAppointment;
                }),
            },

            question: {
                required: requiredIf(function () {
                    return this.action === Actions.Question;
                }),
                minLength: minLength(10),
                maxLength: maxLength(1200),
            },
        },
    },

    data() {
        return {
            dealer: {},

            status: 'loading', // loading, loaded, error, submitting, success

            form: {
                datetime: null,

                templateId: null, // used to track email template (if passed in)

                firstName: '',
                lastName: '',
                email: '',
                phone: '',

                sendTextReminder: false,

                address: '',
                city: '',
                state: '',
                zip: '',

                question: '',
            },

            // when successfully scheduled
            appointment: {
                date: '',
                time: '',
            },
        };
    },

    computed: {
        pleaseSelectHint() {
            if (this.action === Actions.Question)
                return 'Please fill out the form below.';
            return 'Please select a date & time and fill out the form below.';
        },

        formIsInvalid() {
            return this.$v.$invalid;
        },

        /* params */

        arkona() {
            return this.$route.params.arkona;
        },

        oppId() {
            return this.$route.params.oppId;
        },

        // optional param with default to customer selling
        type() {
            return this.$route.params.type || 'sell';
        },

        // optional query param with default
        action() {
            return this.$route.query.action || Actions.DealershipAppointment;
        },

        /* statuses */

        loading() {
            return this.status === 'loading';
        },

        error() {
            return this.status === 'error';
        },

        submitting() {
            return this.status === 'submitting';
        },

        success() {
            return this.status === 'success';
        },
    },

    async created() {
        await sleep(1000);

        if (!this.arkona) {
            this.setError('No arkona in url');
            return;
        }

        try {
            const endpoint = `dealers/arkona/${this.arkona}/true-car`;
            const { data } = await axios.get(endpoint);
            this.dealer = data;

            if (this.dealer) {
                this.setStatus('loaded');
            } else {
                this.setError(`No dealer returned for arkona: ${this.arkona}`);
            }
        } catch (error) {
            this.setError(error);
        }
    },

    methods: {
        setStatus(status) {
            this.status = status;
        },

        setError(error) {
            this.setStatus('error');
            // eslint-disable-next-line no-console
            console.error(new Error(error));
        },

        setSuccess() {
            this.setStatus('success');

            // update display
            if (this.action !== Actions.Question) {
                this.appointment.date = format(this.form.datetime, 'PPP');
                this.appointment.time = format(this.form.datetime, 'p');
            }
        },

        async submit() {
            this.$v.form.$touch();
            if (this.formIsInvalid) return;

            this.setStatus('submitting');
            await sleep(1000);

            const endpoint = 'form/truecar';
            const payload = {
                arkona: this.arkona,
                source: 'dw-truecar',
                oppId: this.oppId,
                type: this.type,
                action: this.action,
                ...this.form,
            };

            try {
                const { data } = await axios.post(endpoint, payload);
                ga('send', 'action-type', this.action);
                this.setSuccess();
            } catch (error) {
                this.setError(error);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.view {
    display: flex;
    flex-direction: column;
    align-items: center;
    & > * {
        margin: auto;
    }
}

.loading-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
}

.error-wrap .title {
    color: $color-error;
}
.form-success-wrap {
    .title {
        color: $color-highlight;
    }
    ul {
        list-style-type: none;
        padding-left: 14px;
        li {
            font-weight: $font-weight-bold;
            span {
                color: lighten($color-font-dark, 25%);
            }
        }
    }
}

.main-wrap {
    width: 100%;
    flex: auto;
    display: flex;
    flex-direction: column;
    align-items: center;

    .hero-image {
        flex: none;
        width: 100%;
        max-width: 1100px;
    }

    .gray-bg {
        width: 100%;
        margin-top: 3rem;
        background-color: $color-bg-gray;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .divot {
            position: absolute;
            top: 0;
            width: 0;
            height: 0;
            border-left: 30px solid transparent;
            border-right: 30px solid transparent;
            border-top: 30px solid $color-font-light;
        }
    }

    .please-select {
        margin-top: 4rem;
        padding: 0 2rem;
        text-align: center;
        font-size: 18px;
        letter-spacing: 0px;
        color: $color-medium-gray;
    }

    .appointment-and-form {
        margin-top: 1rem;
        width: 100%;
        max-width: 54rem;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-around;

        .select-appointment-slot,
        .contact-details-form {
            margin: 0 auto;
        }
    }

    .text-reminder-container {
        .send-text-reminder {
            margin: 0 auto;
        }
    }
}
</style>

<style lang="scss">
.send-text-reminder {
    label {
        font-weight: $font-weight-normal !important;
    }
}
</style>
